import Member from 'iam/models/Member'
import { CancellationToken } from 'infra/cancellation'
import { action, computed, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { Observable, Subject, combineLatest, firstValueFrom } from 'rxjs'
import { distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators'
import { CursorPageQuery, CursorPaged, Id } from 'types/definitions'
import { fromStream, toStream } from 'utils/mobx-util'
import { shareLatest } from 'utils/rx-util'
import { SearchTextViewModel } from 'infra/SearchTextViewModel'
import { SupportingClientSelected } from './DraftChange'
import { DraftStateSlice, PrepareContext } from './DraftStateSlice'
import { StepFactory, StepViewModel } from './OutlineViewModel'
import { IamPort } from './ports'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { UserDataType } from 'iam/models/UserDataType'

const TITLE = 'Supporting clients'

/**
 * Supporting client Selector view model, for selecting the supporting client
 */
export default class SupportingClientSelectorViewModel extends DraftStateSlice {
  /**
   * The singleton step for this slice.
   */
  readonly step: StepViewModel

  /**
   * Changes subject.
   * @private
   */
  private readonly changesSubject = new Subject<SupportingClientSelected>()

  /**
   * Stream for whether the step is valid.
   */
  isValid$ = toStream(() => this.isValid, true)

  /**
   * The search text.
   */
  readonly searchText = new SearchTextViewModel()

  /**
   * Changes stream.
   */
  readonly changes$ = this.changesSubject.asObservable()

  /**
   * The supporting clients selected.
   */
  @observable _selectedSupportingClient: Member[] = []

  /**
   * Emits the selected owner on subscribe, but only after `prepare` has been run.
   */
  readonly selectedSupportingClient$ = toStream(
    () => this.selectedSupportingClient,
    true,
  ).pipe(
    distinctUntilChanged(),
    shareLatest(),
    // Don't start emitting until the slice is ready.
    this.whileReady(),
  )

  /**
   * Streams as MobX Observables.
   */
  private readonly $ = {
    selectedWorkspaceMember: fromStream(null, this.selectedWorkspaceMember$),
    selectedOwner: fromStream(null, this.selectedOwner$),
  }

  /**
   * Constructor.
   *
   * @param isActive$ Observable for whether the slice is active.
   * @param createStep Factory for creating a Step.
   * @param selectedWorkspaceMember$ Observable for the selected workspace member
   * @param selectedOwner$ Observable for the selected job owner
   * @param adapter The IAM adapter.
   * @param _isDisabled Getter for whether the selector is disabled
   * @param _jobWasCreated Getter for whether the selector is visible
   */
  constructor(
    isActive$: Observable<boolean>,
    createStep: StepFactory,
    private selectedWorkspaceMember$: Observable<Member | null>,
    private selectedOwner$: Observable<Member | null>,
    private adapter: IamPort,
    private _isDisabled: () => boolean,
    private _jobWasCreated: () => boolean,
  ) {
    super(isActive$)
    makeObservable(this)
    this.selectedSupportingClient$.subscribe()
    this.changes$.subscribe()

    combineLatest([
      toStream(() => this.scrollProvider, true),
      toStream(() => this.selectedSupportingClient, true),
    ])
      .pipe(
        switchMap(([scrollProvider]) => scrollProvider.fetch()),
        this.whileReady(),
      )
      .subscribe()

    this.searchText.debouncedValue$
      .pipe(this.whileReady())
      .subscribe(() => this.scrollProvider.fetch())

    this.step = createStep({
      stepId: 'client_support_selection',
      isValid: () => Boolean(this._selectedSupportingClient),
      outlineItemDisplay: () => {
        return { title: TITLE }
      },
    })
  }

  /**
   * Whether the step is valid.
   */
  @computed
  get isValid() {
    return Boolean(this.selectedSupportingClient)
  }

  @computed
  get canManageSupportingClients() {
    const member = this.$.selectedWorkspaceMember()
    return member?.hasPermission('ADMIN_JOB_CLIENTS_MANAGE') ?? false
  }

  /**
   * This is the only step container.
   */
  @computed
  get stepContainers() {
    if (!this._jobWasCreated() || !this.canManageSupportingClients) {
      return []
    }

    return [this]
  }

  /**
   * Whether the supporting client selector is disabled.
   */
  @computed
  get disabled() {
    return this._isDisabled()
  }

  /**
   * The scroll provider for Members.
   */
  @computed
  get scrollProvider() {
    return new InfiniteScrollProvider<Member>({
      limit: 20,
      fetchItems: async (params) => this.fetchMembers(params),
      startFetchTaskAsPending: true,
    })
  }

  /**
   * The currently selected supporting client
   */
  @computed
  get selectedSupportingClient() {
    return this._selectedSupportingClient
  }

  /**
   * Hydrates the supporting client state from the Job.
   *
   * @param context
   * @param ct
   */
  async prepare(context: PrepareContext, ct = CancellationToken.default) {
    // Wait for the WS selector to have been prepared.
    await firstValueFrom(
      this.selectedOwner$.pipe(takeUntil(ct.asObservable())),
      { defaultValue: null },
    )

    ct.throwIfCancelled()

    if (context.type === 'EditDraft') {
      this.setClientSupport(context.job.supportingClients.map((c) => c.member))
      return
    }

    this.setClientSupport([])
  }

  /**
   * Sets the selected supporting client.
   *
   * @param clientSupport
   */
  @action.bound
  setClientSupport(clientSupport: Member[]) {
    this._selectedSupportingClient = clientSupport
  }

  /**
   * Add a supporting client.
   *
   * @param clientSupport
   */
  @action.bound
  addClientSupport(clientSupport: Member) {
    this.changesSubject.next({
      member: clientSupport,
      type: 'SupportingClientAdded',
    })
  }

  /**
   * Remove a supporting client.
   *
   * @param clientSupport
   */
  @action.bound
  removeClientSupport(clientSupport: Member) {
    this.changesSubject.next({
      member: clientSupport,
      type: 'SupportingClientRemoved',
    })
  }

  /**
   * Selects the specified owner.
   *
   * @param clientSupports
   */
  @action.bound
  selectClientSupport(clientSupports: Member[]) {
    const toAdd = clientSupports.filter(
      (c) =>
        !this._selectedSupportingClient.find(
          (s) => s.user.publicId === c.user.publicId,
        ),
    )
    const toRemove = this._selectedSupportingClient.filter(
      (s) => !clientSupports.find((c) => c.user.publicId === s.user.publicId),
    )

    toAdd.forEach(this.addClientSupport)
    toRemove.forEach(this.removeClientSupport)
    this.setClientSupport(clientSupports)
  }

  /**
   * Triggers new member search.
   *
   * @param newText
   */
  @action.bound
  triggerSearch(newText: string) {
    this.searchText.change(newText)
  }

  /**
   * Fetches the workspace's members.
   */
  fetchMembers = task.resolved(async (params: CursorPageQuery) => {
    const member = this.$.selectedWorkspaceMember()
    if (!member) {
      return CursorPaged.empty
    }

    if (
      !member.hasPermission('ADMIN_USER_LIST') &&
      !member.hasPermission('ADMIN_CLIENT_USER_LIST')
    ) {
      return CursorPaged.fromArray([member])
    }

    let page = await this.adapter.fetchAvailableClientMembersForWorkspace({
      ...params,
      workspace_id: Id.assert(member.workspace.id),
      search_text: this.searchText.value,
      user_types:
        !member.hasPermission('ADMIN_USER_LIST') &&
        member.hasPermission('ADMIN_CLIENT_USER_LIST')
          ? [UserDataType.User]
          : undefined,
    })

    const selectedOwner = this.$.selectedOwner()
    if (selectedOwner) {
      page = CursorPaged.map(page, (members) =>
        members.filter((m) => m.user.publicId !== selectedOwner.user.publicId),
      )
    }
    return page
  })
}
